import { DeviceErrors } from "@minervaproject/avsubsystem";

import {
  PreclassTestErrorsType,
  PreclassTestNameType,
  PreclassTestOutcomeType,
  PreclassTestResultMessageType,
  PreclassTestResultSeverityType,
  PreclassTestSpecificErrorsType,
} from "../../../shared/types/preclass_check";

import { PreclassCheckResultType } from "shared/types";

const LINKS = {
  chromeAccess:
    "https://help.minervaproject.com/en/articles/4973556-enabling-your-camera-and-microphone-in-chrome",
  computerRequirements: "https://help.minervaproject.com/en/articles/4292732-computer-requirements",
  connectionIssues:
    "https://help.minervaproject.com/en/articles/4296298-how-to-get-the-fastest-internet-connection",
  externalServices: "https://help.minervaproject.com/en/articles/3783642-external-services",
  liveTechSupport:
    "https://help.minervaproject.com/en/articles/4296187-contacting-live-tech-support",
  microphoneIssues: "https://help.minervaproject.com/en/articles/4296318-common-microphone-issues",
  troubleShootingCamera:
    "https://help.minervaproject.com/en/articles/4296180-troubleshooting-issues-with-my-camera",
  troubleShootingMic:
    "https://help.minervaproject.com/en/articles/4296318-troubleshooting-issues-with-my-microphone",
  videoIssues: "https://help.minervaproject.com/en/articles/4296180-common-video-issues",
  minervaHelpUrl: "http://help.minervaproject.com",
  downloadChromeUrl: "https://www.google.com/chrome/",
  minervaExtensionUrl: "https://chrome.google.com/webstore/detail/jioaiecngdmdgcnmjiappopamjjacjeg",
};

const CAMERA_ISSUES_LINK = LINKS.videoIssues;
const CAMERA_IS_IN_USE_LINK = `${CAMERA_ISSUES_LINK}#h_23e49fd838`;
const CHROME_ACCESS_LINK = LINKS.chromeAccess;
const COMPUTER_REQUIREMENTS_LINK = LINKS.computerRequirements;
// const CONNECTION_ISSUES_LINK = LINKS.connectionIssues;
// const EXTERNAL_SERVICES_LINK = LINKS.externalServices;
const LIVE_TECH_SUPPORT_LINK = LINKS.liveTechSupport;
// const MIC_ISSUES_LINK = LINKS.microphoneIssues;
const TROUBLESHOOTING_CAMERA_LINK = LINKS.troubleShootingCamera;
const TROUBLESHOOTING_MIC_LINK = LINKS.troubleShootingMic;

const CHROME_DOWNLOAD_LINK = LINKS.downloadChromeUrl;
// const DEFAULT_ERROR_MESSAGE = "Something went wrong.";
// const DEFAULT_ACTION = "Please restart your browser or computer and try again.";

export class PreclassCheckMessages {
  // Maps are javascript's native ordered dictionary. The order of insertion below is the severity
  // order of the failure. Since the view only shows the most severe error or most informative info, the order is important.

  errorInfoMap: Map<PreclassTestErrorsType, PreclassTestResultMessageType>;

  progressInfoMap: Map<PreclassTestNameType, PreclassTestResultMessageType>;

  constructor() {
    // --------------------------
    // Progress messages
    /// --------------------------
    this.progressInfoMap = new Map();

    this.progressInfoMap.set(PreclassTestNameType.MIC_ACCESS_ALLOWED, {
      message: "Trying to access your camera and microphone.",
      action: "Please allow access if prompted.",
      alertKind: "info",
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    });

    this.progressInfoMap.set(PreclassTestNameType.AUDIO_LEVEL, {
      message: "Testing audio input",
      action: "Please ensure your microphone can detect audio using the input below.",
      alertKind: "info",
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    });

    // --------------------------
    // Browser errors
    // --------------------------
    this.errorInfoMap = new Map();

    this.errorInfoMap.set(PreclassTestSpecificErrorsType.WRONG_BROWSER, {
      message: "Your browser is not supported.",
      action: `Please switch to Google Chrome to join class.`,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
      link: CHROME_DOWNLOAD_LINK,
      alertKind: "error",
    });

    // TODO: Not implemented yet - do we want it?
    this.errorInfoMap.set(PreclassTestSpecificErrorsType.OLD_BROWSER, {
      message: "Your version of Chrome is not supported.",
      action: `Please upgrade to the latest version of Google Chrome to join class. <a target="_blank" href="${CHROME_DOWNLOAD_LINK}">Download</a>`,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(PreclassTestSpecificErrorsType.FAILED_FORUM_REQUEST, {
      message: "Failed to reach our servers.",
      action: "Please contact Live Tech Support.",
      link: LIVE_TECH_SUPPORT_LINK,
      alertKind: "error",
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    });

    this.errorInfoMap.set(PreclassTestSpecificErrorsType.AV_CONNECTION_FATAL, {
      message: "Unable to reach our servers due to network issues.",
      action: "Try strengthening your connection or contact Live Tech Support.",
      link: LIVE_TECH_SUPPORT_LINK,
      alertKind: "error",
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    });

    this.progressInfoMap.set(PreclassTestNameType.CONNECTION, {
      message: "Trying to connect to class.",
      action: "Please wait.",
      alertKind: "info",
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    });

    this.errorInfoMap.set(PreclassTestSpecificErrorsType.AV_CONNECTION_RETRYING, {
      message: "Unable to establish a stable connection. Retrying.",
      action: "Try strengthening your connection or contact Live Tech Support.",
      link: LIVE_TECH_SUPPORT_LINK,
      alertKind: "error",
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    });

    // --------------------------
    // Audio errors
    // --------------------------

    this.errorInfoMap.set(DeviceErrors.AUDIO_MAC_NOT_ALLOWED, {
      message: "Access to your microphone was rejected by your OS.",
      action: `Please allow access to your microphone and refresh this page.`,
      link: TROUBLESHOOTING_MIC_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(DeviceErrors.AUDIO_WINDOWS_CAMERA_IN_USE, {
      message: "Your microphone is in use by another application.",
      action: "Please close that application and refresh this page to continue.",
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(DeviceErrors.AUDIO_NOT_ALLOWED, {
      message: "Access to your microphone was rejected by Chrome.",
      action: `Please allow access to your microphone and refresh this page.`,
      link: CHROME_ACCESS_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(DeviceErrors.AUDIO_NOT_FOUND, {
      message: "No microphone detected.",
      action:
        "Please attach a microphone to your computer, or if already attached, please disconnect and reconnect it.",
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(DeviceErrors.AUDIO_FAIL, {
      message: "There is an issue with your audio.",
      action: `Please restart your browser or computer and try again.`,
      link: TROUBLESHOOTING_MIC_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    // --------------------------
    // Audio detection test (Detects any audio coming out of the mic)
    // --------------------------

    this.errorInfoMap.set(PreclassTestSpecificErrorsType.AUDIO_LEVEL_NOT_DETECTED, {
      message: "No audio input detected.",
      action: `Please ensure your microphone can detect audio using the input below.`,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      link: TROUBLESHOOTING_MIC_LINK,
      alertKind: "error",
    });

    // --------------------------
    // Video errors
    // --------------------------

    this.errorInfoMap.set(DeviceErrors.VIDEO_MAC_NOT_ALLOWED, {
      message: "Access to your camera was rejected by your OS.",
      action: `Please allow access to your camera.`,
      link: TROUBLESHOOTING_CAMERA_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(DeviceErrors.VIDEO_WINDOWS_CAMERA_IN_USE, {
      message: "Your camera is in use by another application.",
      action: `Please close that application and refresh this page to continue.`,
      link: CAMERA_IS_IN_USE_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(DeviceErrors.VIDEO_NOT_ALLOWED, {
      message: "Access to your camera was rejected by Chrome.",
      action: `Please allow access to your camera.`,
      link: CHROME_ACCESS_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(DeviceErrors.VIDEO_NOT_FOUND, {
      message: "No camera detected.",
      action:
        "Please attach a camera to your computer, or if already attached, please disconnect and reconnect it.",
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(DeviceErrors.VIDEO_FAIL, {
      message: "There is an issue with your video.",
      action: `Please restart your browser or computer and try again.`,
      link: TROUBLESHOOTING_CAMERA_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    // --------------------------
    // Compute power issues
    // --------------------------

    // TODO: We don't have a way to disable animations, is it worth it?

    // this.errorInfoMap.set(PreclassTestSpecificErrorsType.ERRORS.LOW_CPU_SCORE, {
    //   message: "Your processor doesn't meet the minimum requirements.",
    //   action: "Animations will be reduced for a better experience." +
    //     ` <a target="_blank" href="${COMPUTER_REQUIREMENTS_LINK}">Learn more</a>.`,
    // });

    // this.errorInfoMap.set(CheckResult.ERRORS.LOW_CPU_CORES, {
    //   message: "Your processor doesn't meet the minimum requirements.",
    //   action: "Animations will be reduced for a better experience." +
    //     ` <a target="_blank" href="${COMPUTER_REQUIREMENTS_LINK}">Learn more</a>.`,
    // });

    this.errorInfoMap.set(PreclassTestSpecificErrorsType.LOW_CPU_CORES, {
      message: "Your processor doesn't meet the minimum requirements.",
      action: "Please use a computer with a faster processor for the best experience.",
      link: COMPUTER_REQUIREMENTS_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(PreclassTestSpecificErrorsType.LOW_CPU_SCORE, {
      message: "Your processor doesn't meet the minimum requirements.",
      action: "Please use a computer with a faster processor for the best experience.",
      link: COMPUTER_REQUIREMENTS_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });

    this.errorInfoMap.set(PreclassTestSpecificErrorsType.LOW_MEMORY, {
      message: "Your memory doesn't meet the minimum requirements.",
      action: "Please use a computer with more memory for the best experience.",
      link: COMPUTER_REQUIREMENTS_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error",
    });
  }

  getHighestPriorityMessageFromResults(results: PreclassCheckResultType) {
    let info = null;
    // get a list of the errors in the PreclassCheckResultType
    const errors = Object.values(results)
      .map((result) => result.error)
      .filter((error) => error);

    for (const [key, value] of this.errorInfoMap) {
      if (errors.includes(key)) {
        info = value;
        break;
      }
    }

    // if there are no errors we get the progress
    if (!info) {
      for (const [key, value] of this.progressInfoMap) {
        if (results[key]?.outcome === PreclassTestOutcomeType.IN_PROGRESS) {
          info = value;
          break;
        }
      }
    }

    // if there are no errors or progress we return null and handle it in the view
    return info;
  }
}

export { LINKS };
